import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";

const EditInvestment = () => {
  const { userId, fdNumber } = useParams();
  const navigate = useNavigate();

  const [investment, setInvestment] = useState(null);
  const [formData, setFormData] = useState({
    startDate: "",
    amount: "",
    interestRate: "",
    durationValue: "",
    durationUnit: "months",
  });
  const [calculatedFields, setCalculatedFields] = useState({
    totalReturnAmount: 0,
    totalInterest: 0,
    maturityDate: null,
    currentValue: 0,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Format date to YYYY-MM-DD for input[type=date]
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0]; // Fixed syntax error here
  };

  useEffect(() => {
    const fetchInvestment = async () => {
      if (!userId || !fdNumber) return;
      try {
        setLoading(true);
        const res = await api.get(
          `/admin/clients/${userId}/investments/${fdNumber}`
        );
        const investmentData = res.data.data;
        setInvestment(investmentData);

        const durationInMonths = investmentData.duration;
        const isYears = durationInMonths % 12 === 0;
        setFormData({
          startDate: formatDateForInput(investmentData.startDate),
          amount: investmentData.amount.toString(),
          interestRate: investmentData.interestRate.toString(),
          durationValue: isYears
            ? (durationInMonths / 12).toString()
            : durationInMonths.toString(),
          durationUnit: isYears ? "years" : "months",
        });

        setCalculatedFields({
          totalReturnAmount: investmentData.totalReturnAmount,
          totalInterest: investmentData.totalInterest,
          maturityDate: investmentData.maturityDate,
          currentValue: investmentData.currentValue,
        });
      } catch (err) {
        setErrorMessage("Failed to load investment data");
        console.error("fetchInvestment error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInvestment();
  }, [userId, fdNumber]);

  useEffect(() => {
    const calculateFields = () => {
      const amount = parseFloat(formData.amount) || 0;
      const interestRate = parseFloat(formData.interestRate) || 0;
      const durationInMonths =
        formData.durationUnit === "years"
          ? parseFloat(formData.durationValue) * 12
          : parseFloat(formData.durationValue) || 0;

      const startDate = formData.startDate
        ? new Date(formData.startDate)
        : new Date();

      if (amount > 0 && interestRate > 0 && durationInMonths > 0) {
        // Simple interest: I = P * R * T (where T is in years)
        const totalInterest =
          amount * (interestRate / 100) * (durationInMonths / 12);
        const totalReturnAmount = amount + totalInterest;

        const maturityDate = new Date(startDate);
        maturityDate.setMonth(maturityDate.getMonth() + durationInMonths);

        setCalculatedFields({
          totalReturnAmount: totalReturnAmount,
          totalInterest: totalInterest,
          maturityDate: maturityDate.toISOString(),
          currentValue: amount, // Preview shows initial amount; actual currentValue from server
        });
      }
    };
    calculateFields();
  }, [formData, investment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "durationUnit") {
      const oldUnit = formData.durationUnit;
      const newUnit = value;
      let newDurationValue = formData.durationValue;
      if (newDurationValue && !isNaN(parseFloat(newDurationValue))) {
        if (oldUnit === "months" && newUnit === "years") {
          newDurationValue = (parseFloat(newDurationValue) / 12).toString();
        } else if (oldUnit === "years" && newUnit === "months") {
          newDurationValue = (parseFloat(newDurationValue) * 12).toString();
        }
      }
      setFormData((prev) => ({
        ...prev,
        durationValue: newDurationValue,
        durationUnit: newUnit,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.startDate) {
      newErrors.startDate = "Start Date is required";
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }
    if (!formData.interestRate || parseFloat(formData.interestRate) <= 0) {
      newErrors.interestRate = "Interest Rate must be greater than 0";
    }
    if (!formData.durationValue || parseFloat(formData.durationValue) <= 0) {
      newErrors.durationValue = "Duration must be greater than 0";
    }
    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setErrorMessage("");
      setSuccessMessage("");

      // Prepare the data for API submission
      const durationInMonths =
        formData.durationUnit === "years"
          ? parseFloat(formData.durationValue) * 12
          : parseFloat(formData.durationValue);
      const investmentData = {
        startDate: formData.startDate,
        amount: parseFloat(formData.amount),
        interestRate: parseFloat(formData.interestRate),
        duration: parseInt(durationInMonths),
      };

      const res = await api.put(
        `/admin/clients/${userId}/investments/${fdNumber}`,
        investmentData
      );

      setInvestment(res.data.data);
      setCalculatedFields({
        totalReturnAmount: res.data.data.totalReturnAmount,
        totalInterest: res.data.data.totalInterest,
        maturityDate: res.data.data.maturityDate,
        currentValue: res.data.data.currentValue,
      });

      setSuccessMessage("Investment updated successfully!");
      setTimeout(() => {
        navigate(`/admin/clients/${userId}/investments/${fdNumber}`);
      }, 1000);
    } catch (err) {
      setErrorMessage(
        err.response?.data?.message || "Failed to update investment"
      );
      console.error("updateInvestment error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading investment data...</div>;
  }

  return (
    <div className="card main-content max-w-xl">
      <div className="card-header">
        <h3>Edit Investment: {fdNumber}</h3>
      </div>
      <div className="card-body">
        {successMessage && (
          <div className="alert alert-success">{successMessage}</div>
        )}
        {errorMessage && (
          <div className="alert alert-danger">{errorMessage}</div>
        )}
        {investment ? (
          <form onSubmit={handleSubmit}>
            <div className="input-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
                {errors.startDate && (
                  <p className="text-red-600">{errors.startDate}</p>
                )}
              </div>
              <div className="form-group">
                <label>Investment Amount (₹)</label>
                <input
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
                {errors.amount && (
                  <p className="text-red-600">{errors.amount}</p>
                )}
              </div>
              <div className="form-group">
                <label>Interest Rate (%)</label>
                <input
                  name="interestRate"
                  type="number"
                  value={formData.interestRate}
                  onChange={handleChange}
                  className="form-control"
                  required
                />
                {errors.interestRate && (
                  <p className="text-red-600">{errors.interestRate}</p>
                )}
              </div>
              <div className="form-group">
                <label>Duration</label>
                <div className="flex gap-2">
                  <input
                    name="durationValue"
                    type="number"
                    value={formData.durationValue}
                    onChange={handleChange}
                    className="form-control flex-grow"
                    required
                  />
                  <select
                    name="durationUnit"
                    value={formData.durationUnit}
                    onChange={handleChange}
                    className="form-control w-32"
                  >
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </select>
                </div>
                {errors.durationValue && (
                  <p className="text-red-600">{errors.durationValue}</p>
                )}
              </div>
            </div>
            <div className="mt-4">
              <h4>Calculated Fields (Preview)</h4>
              <div className="flex flex-col gap-1">
                <span>
                  Total Return Amount: ₹{calculatedFields.totalReturnAmount}
                </span>
                <span>Total Interest: ₹{calculatedFields.totalInterest}</span>
                <span>
                  Maturity Date:{" "}
                  {calculatedFields.maturityDate
                    ? new Date(
                        calculatedFields.maturityDate
                      ).toLocaleDateString()
                    : "N/A"}
                </span>
                <span>Current Value: ₹{calculatedFields.currentValue}</span>
              </div>
            </div>
            <div className="flex mt-3 gap-2">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() =>
                  navigate(`/admin/clients/${userId}/investments/${fdNumber}`)
                }
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        ) : (
          <div className="alert alert-danger">Investment not found</div>
        )}
      </div>
    </div>
  );
};

export default EditInvestment;
